import React, { useState, useEffect, useRef } from 'react';
import './Announcement.css';

const POLL_INTERVAL = 20000; // Check every 20 seconds
const SEEN_KEY = 'perm_seen_announcement';

export default function Announcement() {
  const [announcement, setAnnouncement] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  const checkAnnouncement = async () => {
    try {
      // Cache-bust so we always get the latest version
      const res = await fetch(`/announcement.json?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();

      if (!data.active || !data.id) return;

      // Don't show if already dismissed this specific announcement
      const seen = localStorage.getItem(SEEN_KEY);
      if (seen === data.id) return;

      setAnnouncement(data);
      setVisible(true);
    } catch {
      // Silent — announcement failures should never affect the site
    }
  };

  useEffect(() => {
    // Check immediately on mount
    checkAnnouncement();

    // Then poll every 20 seconds
    timerRef.current = setInterval(checkAnnouncement, POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, []);

  const dismiss = () => {
    if (announcement?.id) {
      localStorage.setItem(SEEN_KEY, announcement.id);
    }
    setVisible(false);
    setTimeout(() => { setAnnouncement(null); setDismissed(true); }, 350);
  };

  if (!announcement || dismissed) return null;

  const TYPE_COLORS = {
    info:    { bg: 'var(--blue-light)',   border: 'var(--blue-dark)',  icon: '#4fa3e8' },
    success: { bg: '#dcfce7',             border: '#16a34a',           icon: '#16a34a' },
    warning: { bg: '#fef9c3',             border: '#d97706',           icon: '#d97706' },
    error:   { bg: '#fef2f2',             border: '#dc2626',           icon: '#dc2626' },
  };
  const colors = TYPE_COLORS[announcement.type] || TYPE_COLORS.info;

  return (
    <div
      className={`ann-popup${visible ? ' ann-popup--visible' : ''}`}
      role="alert"
      style={{ '--ann-border': colors.border, '--ann-bg': colors.bg }}
    >
      <div className="ann-popup__inner">
        {/* Type icon */}
        <div className="ann-popup__icon" style={{ color: colors.icon }}>
          {announcement.type === 'success' && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="9" fill="currentColor" fillOpacity="0.15"/>
              <polyline points="4.5 9 7.5 12 13.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {announcement.type === 'warning' && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 2L16.5 15H1.5L9 2z" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <line x1="9" y1="7" x2="9" y2="10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <circle cx="9" cy="12.5" r="0.9" fill="currentColor"/>
            </svg>
          )}
          {announcement.type === 'error' && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.5"/>
              <line x1="6" y1="6" x2="12" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <line x1="12" y1="6" x2="6" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          )}
          {(!announcement.type || announcement.type === 'info') && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.5"/>
              <line x1="9" y1="8" x2="9" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <circle cx="9" cy="5.5" r="0.9" fill="currentColor"/>
            </svg>
          )}
        </div>

        {/* Text */}
        <div className="ann-popup__text">
          {announcement.title && <p className="ann-popup__title">{announcement.title}</p>}
          {announcement.message && <p className="ann-popup__message">{announcement.message}</p>}
          {announcement.cta && announcement.ctaUrl && (
            <a
              href={announcement.ctaUrl}
              className="ann-popup__cta"
              target={announcement.ctaUrl.startsWith('http') ? '_blank' : '_self'}
              rel="noopener noreferrer"
              onClick={dismiss}
            >
              {announcement.cta} →
            </a>
          )}
        </div>

        {/* Close */}
        <button className="ann-popup__close" onClick={dismiss} aria-label="Dismiss">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="2" y1="2" x2="12" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="12" y1="2" x2="2" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
